import React, { useState, useEffect, useRef } from "react";
import {
  Label,
  Input,
  Search,
  TextArea,
  Checkbox,
  ControlError,
  CardHeader,
  CardContent,
  Card,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Select,
  SelectOption,
  RadioButton,
  SearchIcon,
  TextOverflow,
  ContentLoader,
  TableHeader,
  TableContent
} from "@forge/common";
import { useDispatch, useSelector } from "react-redux";
import { CREATE_COURSE_CONSTANTS, vmTemplateTableHeaders } from "../utils/constants";
import {
  IVAPPVMTable,
  IVAPPVMTableHeader,
  ExistingTemplateProps,
} from "../model/index";
import { AppState } from "../../../../../common/store/combinedReducer";
import "./vm-template.scss";
import "../../create-course/create-course.scss";
import { getAllvAppTemplatesAndVms } from "../store/create-course.action";
import { throttle } from "lodash";

const NewVMFromExistingTemplate = ({
  vmFormData,
  vmFormError,
  handleVMFormChange,
  onTemplateSelect,
}: ExistingTemplateProps) => {
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState<IVAPPVMTable[]>([]);
  const [filterType, setFilterType] = useState<string>("Template Name");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchTrigger, setSearchTrigger] = useState<string>("");
  const [selectedVmsAndVapps, setSelectedVmsAndVapps] =
    useState<IVAPPVMTable | null>(null);
  const [filteredTableData, setFilteredTableData] = useState<IVAPPVMTable[]>(
    []
  );
  const [page, setPage] = useState(1);
  const hasReachedEnd = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isLoadingMore = useRef<boolean>(false);
  const savedScrollTop = useRef<number>(0);
  const [templateTableHeaders, setTemplateTableHeaders] = useState(vmTemplateTableHeaders);
  const [sortBy, setSortBy] = useState<number | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<number | undefined>(undefined);

  const isTableLoading: any = useSelector((state: AppState) => {
    return state.createCourseReducer.vAppTemplatesList.isTableLoading;
  });
  const templatesTableData: any = useSelector((state: AppState) => {
    return state.createCourseReducer.vAppTemplatesList.tableData || {};
  });

  const fetchData = async (page: number, searchType?: string, searchValue?: string, sortBy?: number, sortOrder?: number) => {
    dispatch(getAllvAppTemplatesAndVms(page, 15, searchType, searchValue, sortBy, sortOrder));
  };

  useEffect(() => {
    if (page > 1) {
      const searchType = getSearchType(filterType);
      fetchData(
        page,
        searchTrigger ? searchType : undefined,
        searchTrigger || undefined,
        sortBy,
        sortOrder
      );
    }
  }, [page]);

  const getSearchType = (type: string): string => {
    switch (type) {
      case "Template Name":
        return 'templateName';
      case "OS Type":
        return 'osType';
      case "VM Name":
        return 'vmName';
      default:
        return 'templateName';
    }
  };

  const handleScroll = throttle(() => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

    if (isAtBottom && !hasReachedEnd.current) {
      const totalPages = Math.ceil(
        templatesTableData?.totalRecords / templatesTableData?.pageSize
      );
      if (templatesTableData?.pageNo < totalPages) {
        hasReachedEnd.current = true;
        isLoadingMore.current = true;
        if (containerRef.current) {
          savedScrollTop.current = containerRef.current.scrollTop;
        }
        setPage((prev) => prev + 1);
      }
    } else if (!isAtBottom && hasReachedEnd.current) {
      hasReachedEnd.current = false;
    }
  }, 1000);

  useEffect(() => {
    if (
      templatesTableData &&
      Array.isArray(templatesTableData.vAppTemplates) &&
      templatesTableData.vAppTemplates.length > 0
    ) {
      const transformedData = transformVAppTemplateData(
        templatesTableData.vAppTemplates
      );
      setTableData(transformedData);
      setFilteredTableData(transformedData);
      isLoadingMore.current = false;
    } else {
      setTableData(prevData => prevData.length > 0 ? [] : prevData);
      setFilteredTableData(prevData => prevData.length > 0 ? [] : prevData);
      isLoadingMore.current = false;
    }
  }, [templatesTableData?.vAppTemplates]);

  // Restore scroll position after pagination data is appended
  useEffect(() => {
    if (savedScrollTop.current > 0 && containerRef.current && !isLoadingMore.current) {
      containerRef.current.scrollTop = savedScrollTop.current;
      savedScrollTop.current = 0;
    }
  }, [filteredTableData]);

  const transformVAppTemplateData = (apiResponse: any[]) => {
    return apiResponse.map((template: any, index: number) => {
      const vmDetails = template.vmDetails || {};
      return {
        id: vmDetails.vmId || String(index + 1),
        vAppTemplate: template.vAppTemplateName || "--",
        name: vmDetails.vmName || "--",
        osType: vmDetails.osType || "--",
        osVersion: vmDetails.osVer || "--",
        ram: `${vmDetails.ramValue} ${vmDetails.ramUnit}` || "--",
        ipAddress: vmDetails.ipAddress || "--",
        cpu: vmDetails.cpu ? `${vmDetails.cpu} Cores` : "--",
        storage: vmDetails.storage || "--",
        vmPausable: vmDetails.isPausable === "true" ? "Yes" : "No",
      };
    });
  };
  /**
   * This function handles the change event for the filter type selection.
   * @param e 
   */
  const handleFilterTypeChange = (e: any) => {
    const value = e?.target?.value;
    if (value) {
      setFilterType(value);
    }
    const trimmedQuery = searchQuery.trim(); // Trim before checking
    if (trimmedQuery) {
      setSearchTrigger(trimmedQuery); // Update search trigger
      searchData(value, trimmedQuery);
    }
  };
  const getSortByValue = (columnId: string): number => {
    const sortByMap: Record<string, number> = {
      'vAppTemplate': 0, // TemplateName
      'name': 1,          // VmName
      'osVersion': 2      // GuestOs
    };
    return sortByMap[columnId];
  };
  const getSortOrderValue = (direction: 'asc' | 'desc'): number => {
    return direction === 'asc' ? 0 : 1; // Ascending = 0, Descending = 1
  };
  /**
   * This function performs a search based on the provided type and query.
   * @param type - The type of search (e.g., Template Name, OS Type, VM Name)
   * @param query - The search query string
   * @returns void
   */
  const searchData = (type: string, query: string) => {
    const searchType = getSearchType(type);
    dispatch(getAllvAppTemplatesAndVms(1, 15, searchType, query));
    setPage(1); // Reset to first page when searching
    hasReachedEnd.current = false; // Reset pagination flag
  };

  const onItemSelect = (data: IVAPPVMTable) => {
    setSelectedVmsAndVapps(data);
    if (handleVMFormChange) {
      handleVMFormChange("existingVm", data.name);
    }
    if (onTemplateSelect) {
      onTemplateSelect(data);
    }
  };
  const handleSearchInputChange = (e: any) => {
    let query = e.target?.value || "";
    query = query.trimStart(); // Prevent leading spaces
    setSearchQuery(query); // Only update input value, don't search yet
    // Clear search when input is empty
    if (query === "") {
      setSearchTrigger("");
      setPage(1);
      fetchData(1);
    }
  };
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValue = (e.target as HTMLInputElement).value;
    if (e.key === " " && currentValue.trim() === "") {
      e.preventDefault();
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedQuery = currentValue.trim(); // ✅ Trim the query

      // Handle empty search
      if (!trimmedQuery) {
        setSearchQuery("");
        setSearchTrigger("");
        setPage(1);
        hasReachedEnd.current = false;
        fetchData(1);
        return;
      }

      // Update both states with trimmed value
      setSearchQuery(trimmedQuery);
      setSearchTrigger(trimmedQuery); // ✅ Set search trigger
      searchData(filterType, trimmedQuery);
    }
  };
  useEffect(() => {
    if (isTableLoading) return;
    if (filteredTableData.length > 0) {
      // Check if current selection exists in new data
      const currentExists = selectedVmsAndVapps &&
        filteredTableData.some(item => item.id === selectedVmsAndVapps.id);

      if (!currentExists) {
        // Select first item if current selection doesn't exist
        const firstItem = filteredTableData[0];
        setSelectedVmsAndVapps(firstItem);
        if (handleVMFormChange) {
          handleVMFormChange("existingVm", firstItem.name);
        }
        if (onTemplateSelect) {
          onTemplateSelect(firstItem);
        }
      }
    } else {
      // Clear selection when no data
      if (selectedVmsAndVapps !== null) {
        setSelectedVmsAndVapps(null);
      }
    }
  }, [filteredTableData, isTableLoading]);
  /**
   * This function handles sorting of the VM template table based on the specified column and direction.
   * @param direction 
   * @param id 
   * @returns 
   */
  const handleVmTemplateTableSort = (direction: 'asc' | 'desc' | undefined, id: string) => {
    if (!direction) {
      // Reset sorting
      setSortBy(undefined);
      setSortOrder(undefined);
      const updatedHeaders = templateTableHeaders.map(header => ({
        ...header,
        sort: undefined
      }));
      setTemplateTableHeaders(updatedHeaders);

      // Fetch data without sorting
      const searchType = searchTrigger ? getSearchType(filterType) : undefined;
      fetchData(1, searchType, searchTrigger || undefined);
      setPage(1);
      hasReachedEnd.current = false;
      return;
    }

    // Get sort parameters
    const sortByValue = getSortByValue(id);
    const sortOrderValue = getSortOrderValue(direction);

    // Update sort state
    setSortBy(sortByValue);
    setSortOrder(sortOrderValue);

    // Update header sort indicators
    const updatedHeaders = templateTableHeaders.map(header => ({
      ...header,
      sort: header.id === id ? direction : undefined
    }));
    setTemplateTableHeaders(updatedHeaders);

    // Fetch sorted data from backend
    const searchType = searchTrigger ? getSearchType(filterType) : undefined;
    fetchData(1, searchType, searchTrigger || undefined, sortByValue, sortOrderValue);
    setPage(1);
    hasReachedEnd.current = false;
  };
  /**
   * This function builds a table row for the VM template table based on the provided data, index, and selection state.
   * @param data 
   * @param index 
   * @param isSelected 
   * @returns 
   */
  const buildVmTemplateTableRow = (data: IVAPPVMTable, index: number, isSelected: boolean) => {
    return {
      id: `table-${index}`,
      cell: [
        {
          content: (
            <RadioButton
              className="text-sm cursor-pointer"
              data-testid={`table-row-${index}-select`}
              id={`vm-template-row-${index}-select`}
              disabled={isTableLoading || false}
              value={data.id}
              name="vm-selection"
              checked={isSelected}
              onChange={() => onItemSelect(data)}
            />
          ),
          className: 'table-body-cell !max-w-[70px]'
        },
        {
          content: <TextOverflow>{data.vAppTemplate}</TextOverflow>,
          className: 'table-body-cell !max-w-[110px]'
        },
        {
          content: <TextOverflow>{data.name}</TextOverflow>,
          className: 'table-body-cell !max-w-[110px]'
        },
        {
          content: <TextOverflow>{data.osType}</TextOverflow>,
          className: 'table-body-cell'
        },
        {
          content: <TextOverflow>{data.osVersion}</TextOverflow>,
          className: 'table-body-cell !max-w-[110px]'
        },
        {
          content: <TextOverflow>{data.ram}</TextOverflow>,
          className: 'table-body-cell'
        },
        {
          content: <TextOverflow>{data.cpu}</TextOverflow>,
          className: 'table-body-cell'
        },
        {
          content: <TextOverflow>{data.vmPausable}</TextOverflow>,
          className: 'table-body-cell'
        },
        {
          content: <TextOverflow>{data.ipAddress}</TextOverflow>,
          className: 'table-body-cell !max-w-[100px]'
        },
        {
          content: <TextOverflow>{data.storage}</TextOverflow>,
          className: 'table-body-cell'
        }
      ]
    };
  };
  return (
    <>
      <div className="flex gap-10 mt-4 b-top-1">
        <div className="mt-4 w-[413px]">
          <Label required className="createCourse-label mb-2">
            {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.VM_NAME.LABEL}
          </Label>
          <Input
            id="new-vm-name"
            className={`createCourse-name-input-field ${!vmFormError.vmName ? "!border-none" : ""
              }`}
            data-testid="new-vm-name"
            size="regular"
            placeholder="VM Name"
            value={vmFormData.vmName}
            onChange={(e) => handleVMFormChange("vmName", e.target.value)}
            error={!!vmFormError.vmName}
            autoComplete="off"
          />
          {vmFormError.vmName === "" && (
            <div className="text-sm text-gray-400 mt-1 text-xs">
              Max 15 characters
            </div>
          )}
          {vmFormError.vmName && (
            <ControlError className="text-xs" showIcon={false}>
              {vmFormError.vmName}
            </ControlError>
          )}
          <div className="mt-8">
            <Checkbox
              id="auto-pause"
              className="new-vm-input"
              data-testid="vm-auto-pause"
              checked={vmFormData.vmAutoPause}
              onChange={(e) =>
                handleVMFormChange("vmAutoPause", e.target.checked)
              }
            >
              {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.ALLOW_AUTO_PAUSE}
            </Checkbox>
          </div>
        </div>
        <div className="mt-4 w-[413px]">
          <Label className="createCourse-label mb-2">
            {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.DESCRIPTION.LABEL}
          </Label>
          <TextArea
            id="vm-description"
            className={`createCourse-description-input-field no-resize ${!vmFormError.description ? "!border-none" : ""
              }`}
            data-testid="vm-description"
            placeholder="Description"
            rows={3}
            size="regular"
            value={vmFormData.description}
            onChange={(e) => handleVMFormChange("description", e.target.value)}
            error={vmFormError.description !== ""}
          />
          {vmFormError.description === "" && (
            <div className="text-sm text-gray-400 mt-1 text-xs">
              Max 250 characters
            </div>
          )}
          {vmFormError.description && (
            <ControlError className="text-xs" showIcon={false}>
              {vmFormError.description}
            </ControlError>
          )}
        </div>
      </div>
      <div className="mt-4 b-top-1">
        <Label className="text-sm mt-4 mb-2">
          {CREATE_COURSE_CONSTANTS.LIST_VM_LABEL}
        </Label>
        <div>
          <Card className="border-unset mt-4">
            <CardHeader>
              <div className="grid grid-cols-4 gap-4 items-center w-full">
                <div className="col-span-1 !p-0">
                  <Search
                    id="search-input"
                    data-testid="search-input"
                    className="search-template-input-field !w-[260px] !outline-none"
                    minCharacters={0}
                    defaultValue={searchQuery}
                    onChange={handleSearchInputChange}
                    onKeyDown={handleSearchKeyPress}
                    placeholder="Search on press of enter"
                    size={"regular"}
                  />
                </div>
                <div className="col-span-1 filterBy !p-0">
                  <Select
                    key={`filter-select-${filterType}`}
                    height={1}
                    className="text-sm font-normal !border-none !outline-none filter-by-vapp w-[370px]"
                    id="template-filter"
                    data-testid="template-filter"
                    onChange={(e) => handleFilterTypeChange(e)}
                    placeholder={filterType}
                    // value={filterType}
                    defaultValue={filterType}
                    size="regular"
                  >
                    <SelectOption
                      label={"Template Name"}
                      value={"Template Name"}
                      className="text-sm font-normal"
                    />
                    <SelectOption
                      label={"VM Name"}
                      value={"VM Name"}
                      className="text-sm font-normal"
                    />
                    <SelectOption
                      label={
                        CREATE_COURSE_CONSTANTS.VAPP_TEMPLATE_DETAILS.FILTER
                          .OPTIONS.OS_TYPE
                      }
                      value={"OS Type"}
                      className="text-sm font-normal"
                    />
                  </Select>
                </div>
                <div className="col-span-2 text-sm text-end !p-0">
                  <span>
                    {filteredTableData.length > 0
                      ? `${1}-${filteredTableData.length} of ${templatesTableData?.totalRecords || 0}`
                      : "No results"}
                  </span>
                  {filterType && searchTrigger.trim() && (
                    <>
                      <span className="mr-2 ml-2">|</span>
                      <span className="filter-applied">
                        {"Filter applied by " + filterType}
                      </span>
                    </>
                  )}
                  {(!filterType || !searchTrigger.trim()) && (
                    <>
                      <span className="mr-2 ml-2">|</span>
                      <span className="filter-applied">
                        {"Filter applied by " + filterType}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <div className="relative">
              {isTableLoading && filteredTableData.length > 0 && (
                <div className="absolute inset-0 bg-[#242424]/70 z-10 flex items-center justify-center">
                  <ContentLoader className="margin-xl-low margin-xl-mid margin-xl-high">
                    Loading
                  </ContentLoader>
                </div>
              )}
              <div
                onScroll={handleScroll}
                ref={containerRef}
                className="vapp-temp-table-height"
              >
                <CardContent x="none" y="none">
                  <Table onCard>
                    <TableHeader
                      data={vmTemplateTableHeaders}
                      variant="default"
                      canResize
                      onSort={handleVmTemplateTableSort}
                    />

                    {isTableLoading && filteredTableData.length === 0 ? (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={10} className="text-center !border-none">
                            <ContentLoader className="margin-xl-low margin-xl-mid margin-xl-high">
                              Loading
                            </ContentLoader>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : filteredTableData.length > 0 ? (
                      <TableContent
                        data={filteredTableData.map((data, i) => {
                          const isSelected = selectedVmsAndVapps?.id === data.id;
                          return buildVmTemplateTableRow(data, i, isSelected);
                        })}
                        variant="default"
                      />
                    ) : (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={10} className="text-center !border-none">
                            <div className="min-h-32">
                              <p className="text-center text-sm font-bold vpp-table-border">
                                No data found
                              </p>
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </CardContent>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default NewVMFromExistingTemplate;